import React from 'react'

import { inject, observer } from 'mobx-react'

import { SitePermitStatus } from '~/client/graph'
import FileCategoryIcon from '~/client/src/desktop/views/Attachments/components/AttachmentsList/FileCategoryIcon'
import FileTypeIcon from '~/client/src/desktop/views/Attachments/components/AttachmentsList/FileTypeIcon'
import { AvatarSize } from '~/client/src/shared/components/Avatar/Avatar'
import CompanyProfilePreview from '~/client/src/shared/components/CompanyProfilePreview/CompanyProfilePreview'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import { getAttachmentFileType } from '~/client/src/shared/components/WorkflowCard/fileAttachmentHelpers'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import SharedSitePermitStore from '~/client/src/shared/stores/domain/SharedSitePermit.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { LOCATION_SEPARATOR } from '~/client/src/shared/utils/usefulStrings'

interface FilterValueCompanyProps {
  value: any
  companiesStore?: CompaniesStore
}

const FilterValueCompanyComponent: React.FC<FilterValueCompanyProps> = ({
  value,
  companiesStore,
}) => {
  const companyId = typeof value === 'string' ? value : value?.id
  if (!companyId) return null

  const company = companiesStore?.getCompanyById(companyId)
  if (!company) return null

  return (
    <CompanyProfilePreview
      company={company}
      isMenuHidden={true}
      isPhoneHidden={true}
      shouldShowCompanyType={false}
      avatarSize={AvatarSize.Tiny}
      className="tw:inline-flex"
      contentContainerClassName="tw:text-body-sm tw:text-foreground tw:max-w-[240px]"
    />
  )
}

export const FilterValueCompany = inject('companiesStore')(
  observer(FilterValueCompanyComponent),
)

interface FilterValueUserProps {
  value: any
  projectMembersStore?: ProjectMembersStore
}

const FilterValueUserComponent: React.FC<FilterValueUserProps> = ({
  value,
  projectMembersStore,
}) => {
  const userId = typeof value === 'string' ? value : value?.id
  if (!userId) return null

  const user = projectMembersStore?.getById(userId)
  if (!user) return null

  return (
    <UserProfilePreview
      user={user}
      isUserNameClickable={false}
      isBriefInfoHidden={true}
      withEmployeeId={false}
      showBoldName={false}
      avatarSize={AvatarSize.Tiny}
      className="tw:inline-flex"
      contentContainerClassName="tw:text-body-sm tw:text-foreground tw:max-w-[240px]"
    />
  )
}

export const FilterValueUser = inject('projectMembersStore')(
  observer(FilterValueUserComponent),
)

interface FilterValueLocationProps {
  value: any
  locationAttributesStore?: LocationAttributesStore
}

const FilterValueLocationComponent: React.FC<FilterValueLocationProps> = ({
  value,
  locationAttributesStore,
}) => {
  const attributeId =
    typeof value === 'string' && value.includes(LOCATION_SEPARATOR)
      ? value.split(LOCATION_SEPARATOR)[1]
      : value

  if (!attributeId) return null

  const location = locationAttributesStore?.getById(attributeId)
  if (!location) return null

  return (
    <SitemapAttributeTag
      shouldShowAsTag={true}
      dataObject={location}
      className="tw:inline-flex"
      contentContainerClassName="tw:overflow-hidden tw:text-ellipsis tw:py-0.5 tw:max-w-[270px]"
    >
      <span
        title={location.name}
        className="tw:text-body-sm tw:text-foreground"
      >
        {location.name}
      </span>
    </SitemapAttributeTag>
  )
}

export const FilterValueLocation = inject('locationAttributesStore')(
  observer(FilterValueLocationComponent),
)

interface FilterValueWorkflowTypeProps {
  value: any
  workflowTypesStore?: WorkflowTypesStore
}

const FilterValueWorkflowTypeComponent: React.FC<
  FilterValueWorkflowTypeProps
> = ({ value, workflowTypesStore }) => {
  const typeId = typeof value === 'string' ? value : value?.type || value?.id
  if (!typeId) return null

  const workflowType = workflowTypesStore?.getLastUpdatedTypeByType(typeId)
  if (!workflowType) return null

  return (
    <div className="tw:inline-flex tw:max-w-[270px]">
      <WorkflowTypeIcon
        iconName={workflowType?.iconName}
        size="small"
        className="tw:mr-3"
      />
      <span className="tw:text-body-sm tw:text-foreground tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:inline-block tw:align-middle">
        {workflowType.name}
      </span>
    </div>
  )
}

export const FilterValueWorkflowType = inject('workflowTypesStore')(
  observer(FilterValueWorkflowTypeComponent),
)

interface FilterValueStatusProps {
  value: any
}

export const FilterValueStatus: React.FC<FilterValueStatusProps> = ({
  value,
}) => {
  return (
    <StatusBadge
      status={value as SitePermitStatus}
      isLate={false}
      className="tw:inline-flex"
    />
  )
}

interface FilterValueCategoryProps {
  value: any
  workflowCategoriesStore?: WorkflowCategoriesStore
}

const FilterValueCategoryComponent: React.FC<FilterValueCategoryProps> = ({
  value,
  workflowCategoriesStore,
}) => {
  const categoryId = typeof value === 'string' ? value : value?.id
  if (!categoryId) return null

  const category = workflowCategoriesStore?.getById(categoryId)
  if (!category) return null

  return (
    <div className="tw:inline-flex tw:text-body-sm tw:text-foreground tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:max-w-[270px]">
      {category.name}
    </div>
  )
}

export const FilterValueCategory = inject('workflowCategoriesStore')(
  observer(FilterValueCategoryComponent),
)

interface FilterValueProjectProps {
  value: any
  projectsStore?: ProjectsStore
  sharedSitePermitStore?: SharedSitePermitStore
}

const FilterValueProjectComponent: React.FC<FilterValueProjectProps> = ({
  value,
  projectsStore,
  sharedSitePermitStore,
}) => {
  const projectId = value
  if (!projectId) return null

  const projectName =
    projectsStore?.getProjectById(projectId)?.name ||
    sharedSitePermitStore?.projectFromIdToCodeMap[projectId]

  if (!projectName) return null

  return (
    <div className="tw:inline-flex tw:text-body-sm tw:text-foreground tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:max-w-[270px]">
      {projectName}
    </div>
  )
}

export const FilterValueProject = inject(
  'projectsStore',
  'sharedSitePermitStore',
)(observer(FilterValueProjectComponent))

interface FilterValueFileTypeProps {
  value: any
}

export const FilterValueFileType: React.FC<FilterValueFileTypeProps> = ({
  value,
}) => {
  const category = value
  return (
    <div className="tw:inline-flex tw:text-body-sm tw:text-foreground tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:max-w-[270px]">
      <FileCategoryIcon
        fileCategory={category}
        className="tw:mr-1 form-type-icon-size"
      />
      {category}
    </div>
  )
}

interface FilterValueFileExtensionProps {
  value: any
}

export const FilterValueFileExtension: React.FC<
  FilterValueFileExtensionProps
> = ({ value }) => {
  const fileExtension = value
  const fileType = getAttachmentFileType(fileExtension, '/', true)
  return (
    <div className="tw:inline-flex tw:text-body-sm tw:text-foreground tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:max-w-[270px]">
      <FileTypeIcon
        fileType={fileType}
        className="tw:mr-1 form-type-icon-size"
      />
      {fileType}
    </div>
  )
}
